import React, {
  MouseEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cls } from "cls";
import { Modal, ModalEvents } from "@clarkinc/zest-design-system";
import { SectionGroupItem } from "./Filters";
import { shouldNoFollow } from "./FilterUrlHelpers";
import FastAnchor from "~/common/FastAnchor";
import { FilterModalContext } from "../FilterModalContextProvider";

interface Props {
  filterText: string;
  groupNameEncoded?: string;
  labelImageUrl?: string;
  labelImageWidth?: string;
  groupsWithMultipleSelections: number;
  selectedItemsInGroup?: number;
  groupItem: SectionGroupItem;
  prefetch?: boolean;
  isCustomFilterRange?: boolean;
  customLinkClass?: string;
}

const FilterListItem = ({
  groupItem,
  prefetch,
  labelImageUrl,
  labelImageWidth,
  filterText,
  isCustomFilterRange = false,
  customLinkClass,
}: Props) => {
  const [isSelected, setIsSelected] = useState(groupItem.isSelected);
  const wasSelected = useRef<boolean>(false);

  const noFollow = shouldNoFollow(groupItem.filterUrl) ? "nofollow" : undefined;

  const { compareProductsActive, storedFilterUrl } = useContext(
    FilterModalContext
  );

  const shouldOpenModal = compareProductsActive && storedFilterUrl;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsSelected(!isSelected);
    if (shouldOpenModal) {
      Modal.open("confirm-edit-filters-warning");
      // eslint-disable-next-line no-param-reassign
      storedFilterUrl.current.url = groupItem.filterUrl;
      wasSelected.current = true;
    } else {
      window.location.assign(groupItem.filterUrl);
    }
  };

  useEffect(
    function initFilterNavModalSub() {
      return Modal.subscribe(
        "confirm-edit-filters-warning",
        ModalEvents.CLOSE,
        () => {
          if (wasSelected.current && !storedFilterUrl.current.confirmed) {
            setIsSelected((prev) => !prev);
            wasSelected.current = false;
          }
        }
      );
    },
    [groupItem.filterUrl, storedFilterUrl]
  );

  const isVendor = filterText === "vendor";

  const hasHelpText = groupItem.memberDescription && !isSelected;

  const label = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    import("bootstrap.native").then(({ default: { Popover } }) => {
      if (hasHelpText && label.current) {
        new Popover(label.current, {
          placement: "right",
        });
      }
    });
  });

  const getExtraClassNames = () => {
    return isVendor && groupItem.isSuggested
      ? "px-2-1/2 py-3-1/2 pt-2-1/2 pb-4-1/2 lt:pl-8 lt:pb-3-1/2 lt:pt-3/4 lt:pr-8-1/4"
      : "px-2-1/2 py-2 lt:pr-8-1/4";
  };

  return (
    <li
      className="list-none first:pt-1 lt:first:pt-0"
      data-testid="filter-list-item"
    >
      <FastAnchor
        data-testid={`filter-list-item-checkbox-${groupItem.label}`}
        key={groupItem.encodedValues[0]}
        {...(!isCustomFilterRange && { href: groupItem.filterUrl })}
        rel={noFollow}
        onClick={handleClick}
        prefetch={Boolean(prefetch)}
        wait={200}
        aria-current={isSelected ? "true" : "false"}
        {...(isCustomFilterRange && {
          "aria-disabled": "true",
          tabIndex: -1,
          role: "link",
        })}
        className={cls(
          "block",
          "text-gray-800",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px]",
          isCustomFilterRange ? "pointer-events-none" : "cursor-pointer",
          customLinkClass
        )}
      >
        <label
          data-no-jquery
          ref={label}
          data-suggested={isVendor && groupItem.isSuggested}
          data-content={groupItem.memberDescription}
          data-help-content={!!groupItem.memberDescription}
          className={cls(
            getExtraClassNames(),
            "text-shadow-white",
            "hover:bg-gray-50",
            "border-gray-200",
            "border-solid",
            "border-0",
            "border-t",
            "block",
            "font-normal",
            "text-sm-1/2",
            "leading-4",
            "m-0",
            "pl-2",
            "relative",
            "text-gray-800",
            "lt:border-t-0",
            "lt:pl-8",
            "lt:pr-8-1/4",
            "cursor-pointer"
          )}
        >
          <label className="text-shadow-white block font-normal text-sm-1/2 leading-4 mr-0 mb-0 -ml-7 -mt-2-1/2 p-0 absolute top-1/2 left-full text-gray-800 w-4-1/2 lt:-mt-2 lt:-ml-6 lt:pl-0 lt:left-auto lt:w-3-1/4">
            {isSelected ? (
              <input
                className="hidden"
                type="checkbox"
                autoComplete="off"
                defaultChecked
              />
            ) : (
              <input className="hidden" type="checkbox" autoComplete="off" />
            )}
            <CheckBox
              isSelected={isSelected}
              isCustomFilterRange={isCustomFilterRange}
            />
          </label>
          <span
            data-filter-content="filter-list__content"
            className="text-sm-3/4 lt:text-sm-1/2 flex items-center"
          >
            {labelImageUrl && labelImageWidth ? (
              <>
                <img
                  className="max-w-full align-middle"
                  src={labelImageUrl}
                  width={labelImageWidth}
                  alt="plus"
                />
                {`\u00A0${
                  groupItem.displayAsFraction
                    ? groupItem.labelAsFraction
                    : groupItem.label
                }`}
              </>
            ) : (
              <>
                {groupItem.displayAsFraction
                  ? groupItem.labelAsFraction
                  : groupItem.label}
              </>
            )}
          </span>
          {isVendor && groupItem.isSuggested && (
            <span
              style={{ bottom: 2, color: "#B6511E" }}
              data-suggested-label="suggested-brand"
              className="block text-2xs absolute uppercase"
            >
              Suggested Brand
            </span>
          )}
          {!!groupItem.count && (
            <span className="absolute -mt-2 top-1/2 right-10 pr-1.5 lt:pr-1 lt:right-2-1/2 font-semibold lt:font-normal text-sm-1/2">
              {groupItem.count}
            </span>
          )}
          {groupItem.memberDescription ? (
            <span id="tooltipDesc" className="sr-only">
              {groupItem.memberDescription}
            </span>
          ) : null}
        </label>
      </FastAnchor>
    </li>
  );
};

function CheckBox({
  isSelected,
  isCustomFilterRange,
}: {
  isSelected: boolean;
  isCustomFilterRange: boolean;
}) {
  const top = isSelected ? "0" : "200%";
  return (
    <div
      className={cls(
        "list-check",
        "rounded-full",
        "border-solid",
        "border",
        "cursor-pointer",
        "float-left",
        "m-0",
        "overflow-hidden",
        "pt-full",
        "relative",
        "shadow-none",
        "w-full",
        "transition-all",
        "ease-in-check",
        "duration-1100",
        "lt:rounded",
        isSelected && !isCustomFilterRange
          ? "bg-green-400 border-green-400"
          : "bg-white border-gray-400"
      )}
    >
      {isCustomFilterRange ? (
        <div
          data-testid="custom-range-disabled-style"
          className="absolute border-0 border-b border-solid border-gray-400 inset-0 w-full h-[23px] lt:w-[18px] ml-1.5 mt-0.5 lt:mt-[3px]"
          style={{ transform: "rotate(135deg)" }}
        />
      ) : (
        <div
          className="border-white border-solid border border-t-0 border-r-0 border-b-2 border-l-2 m-auto absolute right-0 left-0 w-1/2 transform origin-center transition-all ease-in-check duration-300"
          style={{
            top,
            bottom: "5%",
            height: "20%",
            transform: "rotate(-45deg) translate3d(0, 0, 0)",
          }}
        />
      )}
    </div>
  );
}

export default FilterListItem;
